import React, { useState, useEffect } from 'react';
import {
    Modal, Select, Form, Card, Avatar, Tag, Button, Space, message
} from 'antd';
import {
    UserOutlined, TeamOutlined, SwapOutlined,
    PhoneOutlined, MailOutlined, CalendarOutlined, EnvironmentOutlined
} from '@ant-design/icons';
import API from '../../../../API/API';
import { formatUSPhone } from "../../../../Utils"

const { Option } = Select;

const normalizeSearchText = (value) => String(value || '').trim().toLowerCase();

const getAgentDisplayName = (agent = {}) => {
    const fullName = [agent.firstName, agent.lastName]
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .join(' ');

    return fullName || agent.userName || agent.email || 'Unnamed Agent';
};

const getAgentInitial = (agent = {}) => (
    getAgentDisplayName(agent).charAt(0).toUpperCase() || 'A'
);

const getAgentSearchText = (agent = {}) => [
    getAgentDisplayName(agent),
    agent.email,
    agent.userName,
    agent.directNumber,
    agent.extension,
    agent.subRole,
    agent.role,
    agent._id
].filter(Boolean).join(' ');

const filterSalesAgentOption = (input, option = {}) => {
    const searchableText = normalizeSearchText(
        option.searchText
        || option.props?.searchText
        || option.label
        || option.props?.label
    );

    return searchableText.includes(normalizeSearchText(input));
};

const TransferLeadModal = ({
    visible,
    onCancel,
    onTransfer,
    lead,
    onRefresh,
    currentAgentId,
    agentMap: initialAgentMap
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [agents, setAgents] = useState([]);
    const [filteredAgents, setFilteredAgents] = useState([]);
    const [selectedAgent, setSelectedAgent] = useState(null);

    useEffect(() => {
        if (visible) {
            loadAllAgents();
            form.resetFields();
            setSelectedAgent(null);
        }
    }, [visible]);

    const loadAllAgents = async () => {
        try {
            const response = await API.userGetAll();
            if (response?.success) {
                const allAgents = response.data
                // .filter(user =>
                //     user.role === "SALES-AGENT" && user.isActive
                // );
                setAgents(allAgents);
                filterAgentsByTierType(allAgents);
            }
        } catch (error) {
            console.error('Error loading agents:', error);
            message.error('Failed to load agents');
        }
    };


    const filterAgentsByTierType = (allAgents) => {
        const currentAgent = currentAgentId?._id || currentAgentId;

        // Filter: Only SALES-AGENT role, exclude current agent and logged in user
        const filtered = allAgents.filter(agent => {
            // Only show SALES-AGENT role
            if (agent?.role !== "SALES-AGENT") {
                return false;
            }

            // Exclude logged in user (can't transfer to yourself)
            if (agent?._id === loggedInUserId) {
                return false;
            }

            // Exclude current lead owner
            if (agent?._id === currentAgent) {
                return false;
            }

            // Include all other sales agents
            return true;
        });

        setFilteredAgents(filtered);
    };


    // ────────────────────────────────────────────────
    //     THIS IS THE IMPORTANT PART — CONDITIONAL API
    // ────────────────────────────────────────────────
    const handleTransfer = async (values) => {
        if (!values.agentId) {
            message.warning('Please select an agent');
            return;
        }

        setLoading(true);
        try {
            const hide = message.loading('Transferring lead...', 0);

            let response;
            const isQueued = lead?.status === "queued" || lead?.queueLead === true;

            if (isQueued) {
                // Queued lead → use the special queue transfer API
                response = await API.transferQueuedLead(lead._id, {
                    targetAgentId: values.agentId,
                    reason: "Manual transfer"   // or values.reason if you add a reason field
                });
            } else {
                // Assigned / normal lead → use existing UpdateLead
                response = await API.UpdateLead(lead._id, {
                    handledBy: values.agentId
                });
            }

            hide();

            if (response?.success || response?.status) {
                message.success('Lead transferred successfully!');
                onTransfer(response.data || lead);
                if (isQueued && onRefresh) {
                onRefresh(); // This will refresh the queue list
            }
                onCancel();  // close modal
            } else {
                message.error(response?.message || response?.error || 'Transfer failed');
            }
        } catch (error) {
            console.error('Transfer error:', error);
            const serverMsg = error.response?.data?.message || error.message;
            message.error(serverMsg || 'Failed to transfer lead');
        } finally {
            setLoading(false);
        }
    };

    const getTierTypeColor = (tierType) => {
        switch (tierType) {
            case 'TIER-1': return 'green';
            case 'TIER-2': return 'blue';
            case 'TIER-3': return 'purple';
            default: return 'default';
        }
    };

    const getSubRoleColor = (subRole) => {
        switch (subRole) {
            case 'TIER-1': return 'success';
            case 'TIER2': return 'processing';
            case 'TIER3': return 'warning';
            default: return 'default';
        }
    };
    const loggedInUserId = JSON.parse(localStorage.getItem('userData') || '{}')?._id || null;
    const phoneResult = formatUSPhone(lead?.personalInfo?.phoneNumber || '');
    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <SwapOutlined />
                    <span>Transfer Lead</span>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            width={800}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button
                    key="transfer"
                    type="primary"
                    loading={loading}
                    onClick={() => form.submit()}
                    icon={<SwapOutlined />}
                >
                    Transfer Lead
                </Button>
            ]}
        >
            {/* ========== ADD THIS CARD FOR LEAD DETAILS ========== */}
            {lead && (
                <Card
                    size="small"
                    style={{
                        marginBottom: 24,
                        borderRadius: 12,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                        border: '1px solid #e6e9f0',
                        backgroundColor: '#ffffff'
                    }}
                >
                    {/* Header with Lead Name and Status */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 16,
                        borderBottom: '1px solid #f0f2f5',
                        paddingBottom: 12
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{
                                width: 4,
                                height: 24,
                                background: lead?.queuePriority === 'High' ? '#ff4d4f' : lead?.queuePriority === 'Medium' ? '#faad14' : '#52c41a',
                                borderRadius: 4
                            }} />
                            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#1a1a1a' }}>
                                {lead?.personalInfo?.fullName || 'Lead Details'}
                            </h3>
                        </div>
                        <Space size={8}>
                            <Tag color={lead?.queuePriority === 'High' ? 'red' : lead?.queuePriority === 'Medium' ? 'orange' : 'green'}
                                style={{ marginRight: 0, borderRadius: 16, padding: '4px 12px' }}>
                                {lead?.queuePriority || 'Medium'} Priority
                            </Tag>
                            <Tag color={lead?.leadStage === 'Voice Mail' ? 'purple' : lead?.leadStage === 'Quoted' ? 'blue' : 'default'}
                                style={{ borderRadius: 16, padding: '4px 12px' }}>
                                {lead?.leadStage || 'New'}
                            </Tag>
                        </Space>
                    </div>

                    {/* Main Content Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                        {/* Left Column - Contact Info */}
                        <div>
                            <div style={{ marginBottom: 12 }}>
                                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    CONTACT INFORMATION
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {lead?.personalInfo?.phoneNumber && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <PhoneOutlined style={{ color: '#52c41a', fontSize: 14 }} />
                                            <span style={{ color: '#262626' }}> {phoneResult.formatted}</span>
                                        </div>
                                    )}
                                    {lead?.personalInfo?.email && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <MailOutlined style={{ color: '#faad14', fontSize: 14 }} />
                                            <span style={{ color: '#262626' }}>{lead.personalInfo.email}</span>
                                        </div>
                                    )}
                                    {lead?.personalInfo?.zipCode && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <EnvironmentOutlined style={{ color: '#ff4d4f', fontSize: 14 }} />
                                            <span style={{ color: '#262626' }}>ZIP: {lead.personalInfo.zipCode}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Vehicle Info */}
                        <div>
                            <div style={{ marginBottom: 12 }}>
                                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    VEHICLE DETAILS
                                </div>
                                {lead?.vehicleInfo ? (
                                    <div style={{
                                        backgroundColor: '#f8f9fc',
                                        padding: 12,
                                        borderRadius: 8,
                                        border: '1px solid #eef2f6'
                                    }}>
                                        <div style={{ fontWeight: 600, color: '#1a1a1a', marginBottom: 4 }}>
                                            {lead.vehicleInfo.year} {lead.vehicleInfo.make} {lead.vehicleInfo.model}
                                        </div>
                                        {lead.vehicleInfo.part && (
                                            <div style={{ fontSize: 13, color: '#595959' }}>
                                                Part: {lead.vehicleInfo.part}
                                            </div>
                                        )}
                                        {lead.vehicleInfo.vin && (
                                            <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                                                VIN: {lead.vehicleInfo.vin}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <span style={{ color: '#8c8c8c' }}>No vehicle details</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Domain & Source Info */}
                    {lead?.domain && (
                        <div style={{
                            marginTop: 8,
                            marginBottom: 16,
                            padding: '8px 12px',
                            backgroundColor: '#fafafa',
                            borderRadius: 6,
                            display: 'inline-block'
                        }}>
                            <span style={{ fontSize: 12, color: '#8c8c8c' }}>Domain: </span>
                            <span style={{ fontSize: 13, color: '#262626', fontWeight: 500 }}>{lead.domain}</span>
                            {lead.leadType && (
                                <Tag size="small" style={{ marginLeft: 8 }}>
                                    {lead.leadType}
                                </Tag>
                            )}
                        </div>
                    )}

                    {/* Footer - Assignment & Payment Info */}
                    <div style={{
                        marginTop: 8,
                        paddingTop: 16,
                        borderTop: '1px solid #f0f2f5',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div style={{ display: 'flex', gap: 24 }}>
                            {lead?.assignedDate && (
                                <div>
                                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>Assigned</span>
                                    <div style={{ fontSize: 13, color: '#262626' }}>
                                        {new Date(lead.assignedDate).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </div>
                                </div>
                            )}
                            {lead?.tierType && (
                                <div>
                                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>Tier</span>
                                    <div style={{ fontSize: 13, color: '#262626' }}>
                                        <Tag color={lead.tierType === 'TIER-1' ? 'green' : lead.tierType === 'TIER-2' ? 'blue' : 'purple'}>
                                            {lead.tierType}
                                        </Tag>
                                    </div>
                                </div>
                            )}
                            {lead?.paymentStatus && (
                                <div>
                                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>Payment</span>
                                    <div style={{ fontSize: 13, color: '#262626' }}>
                                        <Tag color={lead.paymentStatus === 'Paid' ? 'green' : 'orange'}>
                                            {lead.paymentStatus}
                                        </Tag>
                                    </div>
                                </div>
                            )}
                        </div>

                        {lead?.handledBy && (
                            <Tag icon={<UserOutlined />} color="default" style={{ borderRadius: 16, padding: '4px 12px' }}>
                                Agent: {lead.handledBy.firstName} {lead.handledBy.lastName}
                            </Tag>
                        )}
                    </div>

                    {/* Created At - with seconds */}
                    {lead?.createdAt && (
                        <div style={{
                            marginTop: 12,
                            fontSize: 12,
                            color: '#8c8c8c',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8
                        }}>
                            <CalendarOutlined />
                            <span>Created: </span>
                            <span>
                                {new Date(lead.createdAt).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit'
                                })}
                            </span>
                        </div>
                    )}

                    {/* Lead Stage History - if recent */}
                    {lead?.leadStageHistory?.length > 0 && (
                        <div style={{
                            marginTop: 12,
                            fontSize: 12,
                            color: '#8c8c8c',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8
                        }}>
                            <CalendarOutlined />
                            <span>Last stage: {lead.leadStageHistory[0].stage} </span>
                            <span>•</span>
                            <span>{new Date(lead.leadStageHistory[0].changedAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}</span>
                            {lead.leadStageHistory[0].comment && (
                                <>
                                    <span>•</span>
                                    <span>Comment: "{lead.leadStageHistory[0].comment}"</span>
                                </>
                            )}
                        </div>
                    )}
                </Card>
            )}
            {/* ========== END OF LEAD DETAILS CARD ========== */}
            <Form form={form} layout="vertical" onFinish={handleTransfer}>
                <Form.Item
                    label="Select Sales Agent"
                    name="agentId"
                    rules={[{ required: true, message: 'Please select an agent to transfer to' }]}
                >
                    <Select
                        placeholder="Search and select sales agent"
                        showSearch
                        optionFilterProp="searchText"
                        filterOption={filterSalesAgentOption}
                        onChange={(value) => {
                            const agent = filteredAgents?.find(a => a?._id === value);
                            setSelectedAgent(agent || null);
                        }}
                        allowClear
                        style={{ width: '100%' }}
                        optionLabelProp="label"
                    >
                        {filteredAgents.map((agent, index) => {
                            // Determine online/offline status
                            const agentId = agent?._id;
                            const optionValue = agentId || `missing-agent-${index}`;
                            const displayName = getAgentDisplayName(agent);
                            const isOnline = Boolean(agent?.isActive);
                            const isLoggedInUser = agentId === loggedInUserId;
                            const isCurrentLeadAgent = agentId === (currentAgentId?._id || currentAgentId);
                            const isOffline = !isOnline; // 👈 ADD THIS

                            const statusColor = isOnline ? '#52c41a' : '#ff4d4f';
                            const statusText = isOnline ? 'Online' : 'Offline';

                            return (
                                <Option
                                    key={optionValue}
                                    value={optionValue}
                                    label={displayName}
                                    searchText={getAgentSearchText(agent)}
                                    disabled={!agentId || isLoggedInUser || isCurrentLeadAgent || isOffline} // 👈 ADD isOffline HERE
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        {/* Avatar */}
                                        <Avatar size="small">
                                            {getAgentInitial(agent)}
                                        </Avatar>

                                        {/* Name + Status Dot */}
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{ color: isOffline ? '#bfbfbf' : 'inherit' }}>
                                                    {displayName}
                                                </span>
                                                <span
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        backgroundColor: statusColor,
                                                        display: 'inline-block',
                                                        boxShadow: `0 0 4px ${statusColor}`,
                                                        opacity: isOffline ? 0.5 : 1
                                                    }}
                                                    title={statusText}
                                                />
                                            </div>

                                            {/* Extra info: phone + tier */}
                                            <div style={{ fontSize: '12px', color: isOffline ? '#bfbfbf' : '#666' }}>
                                                {agent?.directNumber && (
                                                    <span>
                                                        <PhoneOutlined /> {agent.directNumber}
                                                    </span>
                                                )}
                                                {agent?.subRole && (
                                                    <Tag
                                                        color={getSubRoleColor(agent.subRole)}
                                                        style={{
                                                            marginLeft: 8,
                                                            fontSize: 10,
                                                            opacity: isOffline ? 0.5 : 1
                                                        }}
                                                    >
                                                        {agent.subRole}
                                                    </Tag>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </Option>
                            );
                        })}
                    </Select>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default TransferLeadModal;
